import qs from 'query-string';

import { CrumbItem } from '../components/features/account/common/bread-crumb';

interface QueryParams {
  [key: string]: string | string[] | undefined;
}

/**
 * 如果是 edu.net 的域名，返回 edu.net 的 API地址
 * @returns
 */
export const getAPIBaseUrl = (api: string | undefined) => {
  if (!api) {
    return '';
  }
  const isEduUrl = globalThis?.location?.href?.includes?.('.wukongedu.net');
  return isEduUrl ? api.replace('.wukongacademy.com', '.wukongedu.net') : api;
};

/**
 * 去除url上的某个查询参数
 * @param url
 * @param param
 * @returns
 */
export const removeQueryParam = (url: string, param: string) => {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.delete(param);
  return parsedUrl.toString();
};

/**
 * 解析 URL 查询参数
 * @param url - 可选的 URL 字符串，如果不提供则使用当前页面 URL
 * @returns 解析后的查询参数对象
 */
export const getQueryParams = (url?: string): QueryParams => {
  try {
    // 如果提供了 URL，使用 URL 对象解析
    if (url) {
      const urlObj = new URL(url);
      return qs.parse(urlObj.search.slice(1)) as QueryParams;
    }

    // 在浏览器环境下使用 window.location
    if (typeof window !== 'undefined') {
      return qs.parse(window.location.search.slice(1)) as QueryParams;
    }

    return {};
  } catch (error) {
    console.error('解析 URL 参数失败:', error);
    return {};
  }
};

/**
 * 解码 base64 字符串
 * @param str - 需要解码的 base64 字符串
 * @returns 解码后的字符串
 */
export const decodeBase64 = (str: string) => {
  if (!str) {
    return '';
  }
  try {
    // const bytes = Uint8Array.from(window.atob(str), (c) => c.charCodeAt(0));
    // return new TextDecoder('utf-8').decode(bytes);
    return window.atob(str);
  } catch (error) {
    console.error('Base64 解码失败:', error);
    return str;
  }
};

/**
 * 根据url获取当前域名
 * @returns
 */
export const getCurrentDomain = (domain: string | undefined) => {
  if (!domain) {
    return '';
  }

  // 只有线上环境才进行域名替换逻辑
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'prod') {
    return window.location.href.includes('edu.net')
      ? domain.replace('sch.com', 'edu.net')
      : domain.replace('edu.net', 'sch.com');
  }

  // 非线上环境直接返回传入的域名
  return domain;
};

/**
 * 工具：根据路径生成基础面包屑数组（/a/b-c -> [Home, a, b c]）
 * - 可传入 labelMap 覆盖名称。
 */
export function pathToBreadcrumbs(
  pathname: string,
  opts?: {
    baseHref?: string;
    homeLabel?: React.ReactNode;
    labelMap?: Record<string, React.ReactNode>;
  },
): { items: CrumbItem[]; combineKey: string } {
  const base = opts?.baseHref ?? '';
  if (pathname.startsWith('/zh')) {
    pathname = pathname.replace('/zh', '');
  }
  const parts = pathname
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const items: CrumbItem[] = [];

  items.push({
    label: opts?.homeLabel ?? 'Home',
    href: base + '/',
  });

  let acc = '';
  let combineKey = '';
  for (let i = 0; i < parts.length; i++) {
    acc += '/' + parts[i];
    if (i === parts.length - 1) {
      combineKey += parts[i];
    } else {
      combineKey += parts[i] + '_';
    }
    const raw = decodeURIComponent(parts[i]).replace(/[-_]+/g, ' ');
    const key = parts[i].toLowerCase();
    items.push({
      label:
        opts?.labelMap?.[key] ?? raw.charAt(0).toUpperCase() + raw.slice(1),
      href: base + acc,
    });
  }
  // 最后一项不需要 href
  if (items.length > 0) {
    items[items.length - 1].href = undefined;
  }
  return { items, combineKey };
}

/**
 * 判断路径是否包含完整域名
 */
function hasDomain(path: string): boolean {
  try {
    new URL(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * 直接跳转到老项目（不经过中间件代理）
 */
export const navigateToOldProject = (
  path: string,
  target: string = '_self',
) => {
  const oldProjectDomain = process.env.NEXT_PUBLIC_OLD_STU_CENTER_DOMAIN;

  if (oldProjectDomain) {
    let link = '';
    if (hasDomain(path)) {
      // 如果已经包含完整域名，直接使用
      link = path;
    } else {
      // 如果是相对路径，拼接老项目域名
      link = `${oldProjectDomain}${path}`;
    }

    if (target === '_blank') {
      window.open(link, target);
      return;
    }

    window.location.href = link;
  } else {
    // eslint-disable-next-line no-console
    console.warn('NEXT_PUBLIC_OLD_STU_CENTER_DOMAIN not configured');
    if (target === '_blank') {
      window.open(path, target);
      return;
    }

    window.location.href = path;
  }
};
